import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  matchPath,
  Redirect,
  Route,
  type RouteComponentProps,
  Switch,
} from "react-router-dom";

import { ExperimentsProvider } from "@hopper-b2b/experiments";
import I18nProvider, {
  en_defaultTranslations,
  en_uberTranslations,
  enGB_defaultTranslations,
  getLang,
} from "@hopper-b2b/i18n";
import { PortalCore } from "@hopper-b2b/portal";
import {
  ChatSupportProvider,
  KustomerChatSupportProvider,
  useKustomerConfig,
} from "@hopper-b2b/self-serve";
import {
  COLOR_THEMES,
  installCssConfig,
  getDarkModePreferred,
} from "@hopper-b2b/themes";
import {
  ClientName,
  type Translation,
  TranslationLanguage,
} from "@hopper-b2b/types";
import {
  type ISessionContextProps,
  parsePhoneNumber,
  SessionContextProvider,
  SessionStateEnum,
  TenantContextProvider,
  useDeviceTypes,
  UserSourceProvider,
} from "@hopper-b2b/utilities";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core/styles";
import clsx from "clsx";
import { merge } from "lodash-es";

import "./App.css";
import "./fonts.scss";
import type { UberSessionInfo } from "./api/session/startSession";
import { ProtectedRoute } from "./components/Auth";
import { AuthModule } from "./components/AuthModule";
import AxiosInterceptorWrapper from "./components/AxiosInterceptorWrapper";
import Body from "./components/Body";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { RyanairCallbackConfirmation } from "./components/RyanairCallbackConfirmation";
import { SupportModal } from "./components/SupportModal";
import corePortalConfigFromBasicConfig from "./config";
import { useWindowSize } from "./hooks/useWindowSize";
import { branding } from "./modules/branding";
import { muiDarkTheme, uberDarkVariables } from "./modules/darkTheme";
import { muiTheme, uberVariables } from "./modules/defaultTheme";
import coreStore from "./store";
import config from "./utils/config";
import {
  apiConfig,
  HIDDEN_FOOTER_PATHS,
  HIDDEN_HEADER_PATHS,
  NO_FOOTER_PADDING_PATHS,
  PATH_AUTH,
  PATH_HOME,
  PATH_RYANAIR_CALLBACK,
} from "./utils/urlPaths";

// Uber Feature Flags
export enum FEATURE_FLAGS {
  MAINTENANCE = "uber-maintenance",

  // Fintech Flags
  AIR_CFAR = "uber-cfar",
  AIR_CHFAR = "uber-chfar",
  AIR_DISRUPTION = "uber-disruption",
  AIR_DISRUPTION_EXERCISE_REBOOKING = "uber-air-disruption-exercise-rebooking",
  DISRUPTION_REBOOKING_CONTACT_SUPPORT = "DisruptionRebookingContactSupport",
  AIR_MISSED_CONNECTION = "uber-air-missed-connection",
  AIR_PRICE_FREEZE = "uber-air-price-freeze",
  AIR_PRICE_WATCH = "uber-air-price-watch",
  VIP_SUPPORT = "uber-vip-support",
  // in fintech offering steps, check if the flight is a hackerfare _and_ if the correlated experiment is enabled
  AIR_CFAR_HACKER_FARE = "air-cfar-hacker-fare",
  AIR_CHFAR_HACKER_FARE = "air-chfar-hacker-fare",
  AIR_DISRUPTION_HACKER_FARE = "air-disruption-hacker-fare",

  // Features
  AIR_EXCHANGE = "uber-air-self-serve-exchange",
  APP_DARKMODE = "uber-darkmode",
  AIR_SHOP_V4 = "uber-air-shop-v4",
  LOADING_STEPS = "uber-loading-steps",
  PRICE_PREDICTION = "uber-price-prediction",
  IATA_LOOKUP = "flight-location-iata-lookup",
  HFv2 = "ProvidersExperimentUberHackerFares",
  PRICE_DROP_PROTECTION = "uber-price-drop-protection",
  UBER_TRIPS_CORE_MIGRATION = "UberTripsCoreMigration",
}

export enum EXPERIMENTS {
  AIR_FINTECH_SELECTION = "uber-air-fintech-selection",
  HIDE_AIR_ITINERARY_REVIEW = "uber-air-hide-itinerary-review",
  HIDE_FARE_DETAILS = "uber-air-hide-fare-details",
  HIDE_SEATS = "uber-hide-seats",
  HIDE_AIR_PRICE_FREEZE_HACKER_FARE = "hide-price-freeze-hacker-fares",
  AIR_HACKER_FARE = "ProvidersExperimentUberHackerFares",
  FLIGHT_CARD_UI_UPDATE = "FlightCardUIUpdate",
  FINTECH_PURCHASE_ERROR_STATE = "FintechPurchaseErrorState",
  DEDUPE_FLIGHTS_IN_LIST = "DedupeFlightsInList",
  HORIZONTAL_FARE_DETAILS_CARD = "UberHorizontalFareDetailsCard",
}

const PARTNER_EXPERIMENTS_QUERY_PARAM = "partner_experiments";
const DEFAULT_PARTNER_EXPERIMENTS = "air_fintech_control";

const PARTNER_EXPERIMENTS_SESSION_STORAGE_KEY = "b2bportal-partner-experiments";

const generateClassName = createGenerateClassName({
  productionPrefix: "ptBaseModule",
  seed: "ptBaseModule",
});

const DEFAULT_LOCALE = getLang("en-GB");

export const StyleContext = createContext({});

type UserInfoContext = {
  sessionInfo: UberSessionInfo;
  updateSessionInfo: (info: UberSessionInfo) => void;
};

export const UserContext = createContext<Partial<UserInfoContext>>({});

const setViewWidthAndHeight = (width: string, height: string) => {
  document.body.style.setProperty(`--vw`, width);
  document.body.style.setProperty(`--vh`, height);
};

const App = () => {
  const kustomerConfig = useKustomerConfig(ClientName.UBER, config);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [theme, setTheme] = useState(muiTheme);

  const getColorScheme = () => {
    return getDarkModePreferred() ? COLOR_THEMES.DARK : COLOR_THEMES.LIGHT;
  };

  const isDarkModePreferred =
    isDarkMode && getColorScheme() === COLOR_THEMES.DARK;

  const handleDarkModeChange = useCallback((val: boolean) => {
    setIsDarkMode(val);
  }, []);

  useEffect(() => {
    if (isDarkModePreferred) {
      setTheme(muiDarkTheme);
    }
  }, [isDarkMode, isDarkModePreferred]);

  const corePortalConfigTheme = useMemo(() => {
    return corePortalConfigFromBasicConfig(isDarkModePreferred);
  }, [isDarkModePreferred]);

  installCssConfig(isDarkModePreferred ? uberDarkVariables : uberVariables);

  const [sessionInfo, setSessionInfo] = useState({
    hopperSessionToken: "",
    uberAccessToken: "",
    uberRefreshToken: "",
  } as UberSessionInfo);

  const updateSessionInfo = (sessionInfo: UberSessionInfo) => {
    setSessionInfo(sessionInfo);
  };

  const sessionContext: ISessionContextProps = useMemo(() => {
    switch (!!sessionInfo?.userInfoResponse?.riderId) {
      case true: {
        const parsedPhone = parsePhoneNumber(
          sessionInfo?.userInfoResponse.mobileNumber
        );

        return {
          sessionType: SessionStateEnum.Authenticated,
          isLoggedIn: true,
          email: sessionInfo?.userInfoResponse.email,
          firstName: sessionInfo?.userInfoResponse.firstName,
          lastName: sessionInfo?.userInfoResponse.lastName,
          phoneNumber: parsedPhone.nationalNumber,
          countryCode: parsedPhone.countryCode
            ? `+${parsedPhone.countryCode}`
            : undefined,
        };
      }
      case false:
      default:
        return {
          sessionType: SessionStateEnum.Anonymous,
          isLoggedIn: false,
        };
    }
  }, [sessionInfo]);

  const windowSize = useWindowSize();
  // Add a variable for vh to use for specifying full-screen height
  // 100vh does not work properly on iOS. https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
  setViewWidthAndHeight(
    `${windowSize.width * 0.01}px`,
    `${windowSize.height * 0.01}px`
  );

  const { matchesMobile } = useDeviceTypes();
  const [launchEventSent, setLaunchEventSent] = useState(false);

  const tenantTranslation: Translation = useMemo(() => {
    return {
      [TranslationLanguage.en]: merge(
        en_defaultTranslations,
        en_uberTranslations
      ),
      [TranslationLanguage["en-GB"]]: merge(
        enGB_defaultTranslations,
        en_uberTranslations
      ),
    };
  }, []);

  const isFramed = useCallback(() => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return false;
    }
  }, []);

  const appClasses = useMemo((): string => {
    const classes = ["App-container"];
    if (isFramed()) {
      classes.push("App-Framed");
    }
    if (isDarkModePreferred) {
      classes.push("dark-mode");
    }
    return classes.join(" ");
  }, [isFramed, isDarkModePreferred]);

  const partnerExperiments = useMemo(() => {
    const partnerExperiments = new URLSearchParams(
      window.location.search
    ).getAll(PARTNER_EXPERIMENTS_QUERY_PARAM);

    let partnerExperimentString = partnerExperiments.join(",");

    if (partnerExperimentString) {
      console.debug("Partner supplied experiments", partnerExperiments);
      sessionStorage.setItem(
        PARTNER_EXPERIMENTS_SESSION_STORAGE_KEY,
        partnerExperimentString
      );
    } else {
      partnerExperimentString = sessionStorage.getItem(
        PARTNER_EXPERIMENTS_SESSION_STORAGE_KEY
      );
      console.debug(
        "Retrieving partner experiments from session",
        partnerExperimentString
      );
    }

    return partnerExperimentString || DEFAULT_PARTNER_EXPERIMENTS;
  }, []);

  const renderRyanairConfirmation = useCallback(() => {
    return <RyanairCallbackConfirmation />;
  }, []);

  return (
    // note: can remove this fallback when we move to the public/env.js strategy
    // <LoadScript googleMapsApiKey={config.googleMapsApiKey || ""}>
    <div>
      <I18nProvider
        defaultLng={DEFAULT_LOCALE}
        branding={branding}
        tenantTranslation={tenantTranslation}
      >
        <TenantContextProvider tenantContext={{}}>
          <UserSourceProvider>
            <StyleContext.Provider value={{ theme }}>
              <StylesProvider generateClassName={generateClassName}>
                <ThemeProvider theme={theme}>
                  <UserContext.Provider
                    value={{ sessionInfo, updateSessionInfo }}
                  >
                    <SessionContextProvider sessionContext={sessionContext}>
                      <ExperimentsProvider
                        apiConfig={apiConfig}
                        isLoggedIn={!!sessionInfo?.userInfoResponse?.riderId}
                        partnerExperiments={partnerExperiments}
                      >
                        <AxiosInterceptorWrapper
                          hopperSessionToken={sessionInfo?.hopperSessionToken}
                          uberAccessToken={sessionInfo?.uberAccessToken}
                        />
                        <PortalCore
                          config={corePortalConfigTheme}
                          store={coreStore}
                        >
                          <KustomerChatSupportProvider config={kustomerConfig}>
                            <div className={appClasses}>
                              <Switch>
                                {stripPathLeaf()}
                                <Route
                                  path={PATH_AUTH}
                                  render={(
                                    browserRouterProps: RouteComponentProps
                                  ) => <AuthModule {...browserRouterProps} />}
                                />
                                {/* // callback lives outside of ProtectedRoute for faster load time */}
                                <Route
                                  path={PATH_RYANAIR_CALLBACK}
                                  render={renderRyanairConfirmation}
                                />
                                <Route path="*">
                                  <ProtectedRoute
                                    protectedUrl={PATH_HOME}
                                    protectedContent={(pathname: string) => (
                                      <>
                                        <div
                                          className={clsx("App-content", {
                                            // TODO: verify on all URL paths (that do not render Footer) to see if any of
                                            // them should be included in NO_FOOTER_PADDING_PATHS
                                            "no-padding":
                                              NO_FOOTER_PADDING_PATHS.includes(
                                                pathname
                                              ),
                                          })}
                                        >
                                          <Switch>
                                            <Route path={HIDDEN_HEADER_PATHS} />
                                            <Route
                                              path="*"
                                              render={() => <Header />}
                                            />
                                          </Switch>
                                          <Body
                                            theme={theme}
                                            isFirstSession={
                                              sessionInfo?.isFirstSession ??
                                              false
                                            }
                                            launchEventSent={launchEventSent}
                                            setLaunchEventSent={
                                              setLaunchEventSent
                                            }
                                            className={clsx({
                                              //Only add top margin on paths with header
                                              "desktop-top-margin":
                                                !matchesMobile &&
                                                !matchPath(
                                                  pathname,
                                                  HIDDEN_HEADER_PATHS
                                                ),
                                            })}
                                            handleDarkModeChange={
                                              handleDarkModeChange
                                            }
                                            isDarkModePreferred={
                                              isDarkModePreferred
                                            }
                                          />
                                        </div>
                                        <Route
                                          render={(props) => {
                                            if (
                                              HIDDEN_FOOTER_PATHS.includes(
                                                props.location.pathname
                                              )
                                            ) {
                                              return null;
                                            }
                                            return (
                                              <Footer
                                                pathname={
                                                  props.location.pathname
                                                }
                                              />
                                            );
                                          }}
                                        />
                                        <SupportModal />
                                      </>
                                    )}
                                  />
                                </Route>
                              </Switch>
                            </div>
                          </KustomerChatSupportProvider>
                        </PortalCore>
                      </ExperimentsProvider>
                    </SessionContextProvider>
                  </UserContext.Provider>
                </ThemeProvider>
              </StylesProvider>
            </StyleContext.Provider>
          </UserSourceProvider>
        </TenantContextProvider>
      </I18nProvider>
    </div>
    // </LoadScript>
  );
};

export default App;

const stripPathLeaf = () => {
  return (
    <>
      {/* note: standardize the path to have EXACTLY ONE trailing slash WITHOUT index.html suffix */}
      <Route
        exact
        strict
        path="/:url*//*"
        render={(props: RouteComponentProps) => {
          return (
            <Redirect
              push={false}
              to={{
                ...props.location,
                pathname: props.location.pathname.replace(/\/+$/, "") + "/",
              }}
            />
          );
        }}
      />
      <Route
        exact
        path="/:url*/index.html/"
        render={(props: RouteComponentProps) => {
          return (
            <Redirect
              push={false}
              to={{
                ...props.location,
                pathname: props.location.pathname.slice(
                  0,
                  -"index.html/".length
                ),
              }}
            />
          );
        }}
      />
      <Route
        exact
        path="/:url*/index.html"
        render={(props: RouteComponentProps) => {
          return (
            <Redirect
              push={false}
              to={{
                ...props.location,
                pathname: props.location.pathname.slice(
                  0,
                  -"index.html".length
                ),
              }}
            />
          );
        }}
      />
      <Route
        exact
        strict
        path="/:url*"
        render={(props: RouteComponentProps) => {
          return (
            <Redirect
              push={false}
              to={{
                ...props.location,
                pathname: props.location.pathname + "/",
              }}
            />
          );
        }}
      />
    </>
  ).props.children;
  // a solution so that Switch respects Route's from a Fragment; see https://github.com/ReactTraining/react-router/issues/5785
};
